import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { API_BASE_URL } from '../helpers/api';
import { getSingleRecommendationArticle, updateRecommendationArticle, createRecommendationArticle, getDifficultyOptions } from '../helpers/recommendation';
import { deleteMedia, uploadMultipleMedia } from '../helpers/upload';
import { getEstablishments } from '../helpers/establishments';
import { getEvents } from '../helpers/events';
import SearchableSelect from '../components/SearchableSelect';

const RecommendationArticleEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const editorRef = useRef(null);
  const [establishments, setEstablishments] = useState([]);
  const [events, setEvents] = useState([]);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({
    type: 'spot',
    spot: '',
    event: '',
    description: '',
    visitDuration: '',
    order: 0
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    coverImage: null,
    gallery: [],
    duration: '',
    difficulty: 'Facile',
    featureOrder: 0,
    locations: []
  });

  const establishmentOptions = establishments.map(est => ({
    value: est.id,
    label: est.name
  }));

  const eventOptions = events.map(evt => ({
    value: evt.id,
    label: evt.name
  }));

  const difficultyOptions = getDifficultyOptions();

  useEffect(() => {
    Promise.all([
      fetchEstablishments(),
      fetchEvents()
    ]).then(() => {
      if (id !== 'new') {
        fetchArticle();
      } else {
        setIsLoading(false);
      }
    });
  }, [id]);

  const fetchEstablishments = async () => {
    try {
      const data = await getEstablishments();
      setEstablishments(data);
    } catch (err) {
      console.error('Error fetching establishments:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchArticle = async () => {
    try {
      const data = await getSingleRecommendationArticle(id);
    //   console.log(data)
      
      setFormData({
        title: data.title || '',
        description: data.description || '',
        content: data.content || '',
        coverImage: data.coverImage || null,
        gallery: data.gallery || [],
        duration: data.duration || '',
        difficulty: data.difficulty || 'easy',
        featureOrder: data.featureOrder || 0,
        locations: data.locations || []
      });
    } catch (err) {
      console.error('Erreur lors du chargement de l\'article:', err);
      setError('Erreur lors du chargement de l\'article');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleNewLocationChange = (e) => {
    const { name, value, type } = e.target;
    setNewLocation(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...formData.locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      [field]: field === 'order' ? Number(value) : value
    };
    
    setFormData(prev => ({
      ...prev,
      locations: updatedLocations
    }));
  };

  const addLocation = () => {
    // Create a properly formatted location object that matches the API expectations
    const locationToAdd = {
      type: newLocation.type,
      description: newLocation.description,
      visitDuration: newLocation.visitDuration,
      order: newLocation.order
    };
    
    // Map the appropriate field based on the location type
    if (newLocation.type === 'spot') {
      locationToAdd.establishment = newLocation.spot; // Map spot to establishment for API
      locationToAdd.event = null; // Ensure event is null when type is spot
    } else {
      locationToAdd.event = newLocation.event;
      locationToAdd.establishment = null; // Ensure establishment is null when type is event
    }
    
    // Add the new location to the existing locations array
    const updatedLocations = [...formData.locations, locationToAdd];
    
    setFormData(prev => ({
      ...prev,
      locations: updatedLocations
    }));
    
    // Reset new location form
    setNewLocation({
      type: 'spot',
      spot: '',
      event: '',
      description: '',
      visitDuration: '',
      order: 0
    });
    setShowAddLocationForm(false);
  };

  const removeLocation = (index) => {
    const updatedLocations = [...formData.locations];
    updatedLocations.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      locations: updatedLocations
    }));
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const uploadedImage = await uploadMultipleMedia([file]);
      
      setFormData(prev => ({
        ...prev,
        coverImage: uploadedImage[0]
      }));
    } catch (err) {
      console.error('Erreur lors du téléchargement de l\'image de couverture:', err);
      setError('Erreur lors du téléchargement de l\'image de couverture');
    }
  };

//   const handleGalleryUpload = async (e) => {
//     const files = Array.from(e.target.files);
//     if (files.length === 0) return;

//     try {
//       const uploadedImages = await uploadMultipleMedia(files);
      
//       setFormData(prev => ({
//         ...prev,
//         gallery: [...prev.gallery, ...uploadedImages]
//       }));
//     } catch (err) {
//       console.error('Erreur lors du téléchargement des images de la galerie:', err);
//       setError('Erreur lors du téléchargement des images de la galerie');
//     }
//   };

  const handleDeleteCoverImage = async () => {
    if (!formData.coverImage) return;
    
    try {
      await deleteMedia(formData.coverImage.id);
      setFormData(prev => ({
        ...prev,
        coverImage: null
      }));
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'image de couverture:', err);
      setError('Erreur lors de la suppression de l\'image de couverture');
    }
  };

//   const handleImageDelete = async (imageId) => {
//     try {
//       await deleteMedia(imageId);
//       setFormData(prev => ({
//         ...prev,
//         gallery: prev.gallery.filter(img => img.id !== imageId)
//       }));
//     } catch (err) {
//       console.error('Erreur lors de la suppression de l\'image:', err);
//       setError('Erreur lors de la suppression de l\'image');
//     }
//   };

//   const handleGalleryReorder = (newGallery) => {
//     setFormData(prev => ({
//       ...prev,
//       gallery: newGallery
//     }));
//   };

const stripSystemFields = (data) => {
  if (!data) return data;
  
  // Fields to strip completely
  const fieldsToStrip = ['id', 'documentId', 'createdAt', 'updatedAt', 'publishedAt'];
  
  // Fields where we need to keep the id (relations)
  const relationFields = ['coverImage', 'gallery', 'establishment', 'event'];
  
  if (Array.isArray(data)) {
    return data.map(item => stripSystemFields(item));
  }
  
  if (typeof data === 'object' && data !== null) {
    const cleaned = {};
    
    for (const [key, value] of Object.entries(data)) {
      // Skip system fields
      if (fieldsToStrip.includes(key)) continue;
      
      // Handle relation fields
      if (relationFields.includes(key)) {
        if (value === null) {
          cleaned[key] = null;
        } else if (Array.isArray(value)) {
          // Handle array relations (many-to-many)
          cleaned[key] = value.map(item => {
            if (item && typeof item === 'object' && 'id' in item) {
              return { id: item.id };
            }
            return item;
          });
        } else if (typeof value === 'object' && 'id' in value) {
          // Handle single relations (one-to-one, many-to-one)
          cleaned[key] = { id: value.id };
        } else if (typeof value === 'number' || typeof value === 'string') {
          // If value is just a number or string, convert to relation object
          cleaned[key] = { id: value };
        } else {
          cleaned[key] = value;
        }
        continue;
      }
      
      // Special handling for locations array
      if (key === 'locations' && Array.isArray(value)) {
        cleaned[key] = value.map(loc => {
          if (!loc) return loc;
          
          const cleanedLoc = {};
          
          // Handle each field in the location
          for (const [locKey, locValue] of Object.entries(loc)) {
            // Skip system fields in the location itself
            if (fieldsToStrip.includes(locKey)) continue;
            
            // Handle establishment and event relations
            if (locKey === 'establishment' || locKey === 'event') {
              if (locValue === null) {
                cleanedLoc[locKey] = null;
              } else if (typeof locValue === 'object' && 'id' in locValue) {
                cleanedLoc[locKey] = { id: locValue.id };
              } else if (typeof locValue === 'number' || typeof locValue === 'string') {
                cleanedLoc[locKey] = { id: locValue };
              } else {
                cleanedLoc[locKey] = null;
              }
            } else if (typeof locValue === 'object' && locValue !== null) {
              // For other object fields, process recursively
              cleanedLoc[locKey] = stripSystemFields(locValue);
            } else {
              // For primitive fields, just copy them
              cleanedLoc[locKey] = locValue;
            }
          }
          
          return cleanedLoc;
        });
        continue;
      }
      
      // Handle regular fields recursively
      if (Array.isArray(value)) {
        cleaned[key] = value.map(item =>
          typeof item === 'object' && item !== null ? stripSystemFields(item) : item
        );
      } else if (typeof value === 'object' && value !== null) {
        cleaned[key] = stripSystemFields(value);
      } else {
        cleaned[key] = value;
      }
    }
    
    return cleaned;
  }
  
  return data;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitDisabled(true);

    try {
      // Get content from editor if available
      let updatedFormData = { ...formData };
      if (editorRef.current) {
        updatedFormData.content = editorRef.current.getContent();
      }

      // Clean the data for API submission
      const cleanedData = stripSystemFields(updatedFormData);

      if (id === 'new') {
        // Create new article
        await createRecommendationArticle(cleanedData);
        navigate('/recommendation-articles', {
          state: {
            message: 'Article créé avec succès'
          }
        });
      } else {
        // Update existing article
        await updateRecommendationArticle(id, cleanedData);
        navigate('/recommendation-articles', {
          state: {
            message: 'Article mis à jour avec succès'
          }
        });
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Erreur lors de la sauvegarde');
      setSubmitDisabled(false);
    } finally {
      setSubmitDisabled(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {id === 'new' ? 'Nouvel article de recommandation' : 'Modifier l\'article de recommandation'}
        </h2>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/recommendation-articles')}
        >
          Retour à la liste
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row mb-4">
              <div className="col-md-8">
                <div className="mb-3">
                  <label className="form-label">Titre</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Contenu</label>
                  <Editor
                    apiKey="votre-clé-api-tinymce"
                    onInit={(evt, editor) => editorRef.current = editor}
                    initialValue={formData.content}
                    init={{
                      height: 400,
                      menubar: false,
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                      ],
                      toolbar: 'undo redo | blocks | ' +
                        'bold italic forecolor | alignleft aligncenter ' +
                        'alignright alignjustify | bullist numlist outdent indent | ' +
                        'removeformat | help',
                      content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Durée</label>
                    <input
                      type="text"
                      className="form-control"
                      name="duration"
                      value={formData.duration}
                      onChange={handleChange}
                      placeholder="ex: 2-3 heures"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Difficulté</label>
                    <select
                      className="form-select"
                      name="difficulty"
                      value={formData.difficulty}
                      onChange={handleChange}
                    >
                      {difficultyOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* <div className="col-md-4">
                    <label className="form-label">Ordre d'affichage</label>
                    <input
                      type="number"
                      className="form-control"
                      name="featureOrder"
                      value={formData.featureOrder}
                      onChange={handleChange}
                      min="0"
                    />
                  </div> */}
                </div>
              </div>

              <div className="col-md-4">
                <div className="mb-4">
                  <label className="form-label">Image de couverture</label>
                  {formData.coverImage ? (
                    <div className="position-relative mb-3">
                      <img
                        src={API_BASE_URL + (formData.coverImage.formats?.medium?.url || formData.coverImage.url)}
                        alt=""
                        className="img-fluid rounded mb-2"
                        style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                        onClick={handleDeleteCoverImage}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="mb-3">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleCoverImageUpload}
                      />
                    </div>
                  )}
                </div>

                {/* <div className="mb-4">
                  <label className="form-label">Galerie d'images</label>
                  <input
                    type="file"
                    className="form-control mb-3"
                    multiple
                    accept="image/*"
                    onChange={handleGalleryUpload}
                  />
                  <ReorderableGallery
                    images={formData.gallery}
                    baseUrl={API_BASE_URL}
                    onDelete={handleImageDelete}
                    onReorder={handleGalleryReorder}
                  />
                </div> */}
              </div>
            </div>

            {/* Lieux associés */}
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Lieux associés</h5>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowAddLocationForm(!showAddLocationForm)}
                >
                  {showAddLocationForm ? 'Annuler' : 'Ajouter un lieu'}
                </button>
              </div>
              <div className="card-body">
                {showAddLocationForm && (
                  <div className="card bg-light mb-3">
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label">Type</label>
                          <select
                            className="form-select"
                            name="type"
                            value={newLocation.type}
                            onChange={handleNewLocationChange}
                          >
                            <option value="spot">Spot</option>
                            <option value="event">Événement</option>
                          </select>
                        </div>
                        <div className="col-md-8">
                          {newLocation.type === 'spot' ? (
                            <div>
                              <label className="form-label">Spot</label>
                              <SearchableSelect
                                options={establishmentOptions}
                                value={newLocation.spot}
                                onChange={handleNewLocationChange}
                                name="spot"
                                placeholder="Rechercher un spot..."
                                required={true}
                              />
                              {/* <select
                                className="form-select"
                                name="spot"
                                value={newLocation.spot}
                                onChange={handleNewLocationChange}
                                required
                              >
                                <option value="">Sélectionner un spot</option>
                                {establishments.map(est => (
                                  <option key={est.id} value={est.id}>
                                    {est.name}
                                  </option>
                                ))}
                              </select> */}
                            </div>
                          ) : (
                            <div>
                              <label className="form-label">Événement</label>
                              <SearchableSelect
                                options={eventOptions}
                                value={newLocation.event}
                                onChange={handleNewLocationChange}
                                name="event"
                                placeholder="Rechercher un événement..."
                                required={true}
                              />
                              {/* <select
                                className="form-select"
                                name="event"
                                value={newLocation.event}
                                onChange={handleNewLocationChange}
                                required
                              >
                                <option value="">Sélectionner un événement</option>
                                {events.map(evt => (
                                  <option key={evt.id} value={evt.id}>
                                    {evt.name}
                                  </option>
                                ))}
                              </select> */}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-8">
                          <label className="form-label">Description</label>
                          <input
                            type="text"
                            className="form-control"
                            name="description"
                            value={newLocation.description}
                            onChange={handleNewLocationChange}
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Durée de visite</label>
                          <input
                            type="text"
                            className="form-control"
                            name="visitDuration"
                            value={newLocation.visitDuration}
                            onChange={handleNewLocationChange}
                            placeholder="ex: 1h"
                          />
                        </div>
                        <div className="col-md-2">
                          <label className="form-label">Ordre</label>
                          <input
                            type="number"
                            className="form-control"
                            name="order"
                            value={newLocation.order}
                            onChange={handleNewLocationChange}
                            min="0"
                          />
                        </div>
                      </div>

                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={addLocation}
                          disabled={!((newLocation.type === 'spot' && newLocation.spot) || 
                                    (newLocation.type === 'event' && newLocation.event))}
                        >
                          Ajouter ce lieu
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {formData.locations.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Type</th>
                          <th>Nom</th>
                          <th>Description</th>
                          <th>Durée visite</th>
                          <th>Ordre</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.locations.map((location, index) => (
                          <tr key={index}>
                            <td>{location.type === 'spot' ? 'Spot' : 'Événement'}</td>
                            <td>
                              {location.type === 'spot' 
                                ? establishments.find(e => e.id === (location.establishment?.id || location.establishment))?.name || '-'
                                : events.find(e => e.id === (location.event?.id || location.event))?.name || '-'
                              }
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={location.description || ''}
                                onChange={(e) => handleLocationChange(index, 'description', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={location.visitDuration || ''}
                                onChange={(e) => handleLocationChange(index, 'visitDuration', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                value={location.order || 0}
                                onChange={(e) => handleLocationChange(index, 'order', e.target.value)}
                                min="0"
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => removeLocation(index)}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    Aucun lieu associé à cet article
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/recommendation-articles')}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitDisabled}
              >
                {submitDisabled ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RecommendationArticleEdit;